import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import { Megaphone, Users, Linkedin, Instagram, Mail, Copy, Send, Loader2, CheckCircle2, Share2, MessageSquareQuote } from "lucide-react";

function ShareButtons({ url, title, slogan }) {
  const enc = encodeURIComponent(url);
  // LinkedIn share text: title + slogan + URL (pre-filled in user post)
  const linkedinText = slogan ? `${title}\n\n${slogan}` : `Je viens de signer cette pétition : ${title}. Rejoignez-nous !`;
  const txt = encodeURIComponent(linkedinText);
  const txtShort = encodeURIComponent(slogan ? `${title} — ${slogan}` : `Je viens de signer cette pétition : ${title}. Rejoignez-nous !`);
  const linkedin = `https://www.linkedin.com/sharing/share-offsite/?url=${enc}&text=${txt}`;
  const twitter = `https://twitter.com/intent/tweet?text=${txtShort}&url=${enc}`;
  const whatsapp = `https://wa.me/?text=${txtShort}%20${enc}`;
  const email = `mailto:?subject=${encodeURIComponent(title)}&body=${txt}%0A%0A${enc}`;
  const copy = () => { navigator.clipboard.writeText(url); toast.success("Lien copié — collez-le dans votre Story/Bio Instagram."); };
  const onInstagram = (e) => {
    e.preventDefault();
    // Instagram has no public web-share endpoint. On mobile, deep-link to the app; on desktop, copy URL.
    navigator.clipboard.writeText(url);
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isMobile) {
      toast.success("Lien copié — collez-le dans votre Story Instagram !");
      window.location.href = "instagram://story-camera";
    } else {
      toast.success("Lien copié ! Ouvrez Instagram sur mobile pour le partager en Story.");
    }
  };
  return (
    <div className="flex flex-wrap gap-2">
      <a href={linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-[#0A66C2] px-3 py-1.5 text-xs font-bold text-white hover:opacity-90" data-testid="share-linkedin"><Linkedin className="h-3.5 w-3.5" /> LinkedIn</a>
      <a href={twitter} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-black px-3 py-1.5 text-xs font-bold text-white hover:opacity-90" data-testid="share-twitter"><Share2 className="h-3.5 w-3.5" /> X</a>
      <a href={whatsapp} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-3 py-1.5 text-xs font-bold text-white hover:opacity-90" data-testid="share-whatsapp">WhatsApp</a>
      <a href="https://www.instagram.com/" onClick={onInstagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-tr from-[#feda75] via-[#fa7e1e] to-[#d62976] px-3 py-1.5 text-xs font-bold text-white hover:opacity-90" data-testid="share-instagram"><Instagram className="h-3.5 w-3.5" /> Instagram</a>
      <a href={email} className="inline-flex items-center gap-1.5 rounded-full bg-neutral-700 px-3 py-1.5 text-xs font-bold text-white hover:opacity-90" data-testid="share-email"><Mail className="h-3.5 w-3.5" /> Email</a>
      <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-3 py-1.5 text-xs font-bold text-neutral-700 hover:bg-neutral-50" data-testid="share-copy"><Copy className="h-3.5 w-3.5" /> Copier le lien</button>
    </div>
  );
}

function ProgressBar({ current, goal, theme }) {
  const pct = goal > 0 ? Math.min(100, Math.round((current / goal) * 100)) : 0;
  return (
    <div data-testid="petition-progress">
      <div className="flex items-end justify-between text-xs">
        <div className="font-bold text-navy">
          <span className="text-2xl font-serif" style={{ color: theme }}>{current.toLocaleString("fr")}</span>
          <span className="ml-1 text-neutral-500"> / {goal.toLocaleString("fr")} signatures</span>
        </div>
        <div className="font-bold" style={{ color: theme }}>{pct}%</div>
      </div>
      <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-neutral-100">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: theme }} />
      </div>
    </div>
  );
}

function initials(a, b) { return `${(a || "?")[0]}${(b || "")[0]}`.toUpperCase(); }

export default function PetitionDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [signed, setSigned] = useState(false);
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", organization: "", message: "", allow_publish_name: true, newsletter_optin: true });
  const [sending, setSending] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";

  const load = useCallback(() => api.get(`/petitions/${slug}`).then((r) => setP(r.data)).catch(() => setP(false)), [slug]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post(`/petitions/${slug}/sign`, form);
      toast.success("Merci pour votre soutien ! Un email de confirmation vient de partir.");
      setSigned(true);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSending(false); }
  };

  if (p === false) return <PublicLayout><div className="mx-auto max-w-3xl py-20 text-center text-neutral-500">Pétition introuvable.</div></PublicLayout>;
  if (!p) return <PublicLayout><div className="mx-auto max-w-3xl py-20 text-center text-neutral-500">Chargement…</div></PublicLayout>;

  const theme = p.theme_color || "#9C2C44";

  return (
    <PublicLayout>
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${theme}11, transparent)` }}>
        <div className="mx-auto grid max-w-6xl gap-10 px-6 py-16 md:grid-cols-[1.4fr_1fr] md:px-8">
          <div>
            <Link to="/soutenez-nous" className="inline-flex items-center gap-1 text-xs font-semibold text-wine-700 hover:underline">← Toutes les pétitions</Link>
            <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-wine-100 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-wine-700"><Megaphone className="h-3 w-3" /> Pétition La WineTech</div>
            <h1 className="mt-3 font-serif text-3xl text-navy md:text-5xl">{p.title}</h1>
            {p.share_slogan && <p className="mt-2 text-sm italic text-neutral-600">« {p.share_slogan} »</p>}
            <div className="mt-4 rounded-2xl border border-neutral-100 bg-white p-4 shadow-sm">
              <ProgressBar current={p.signatures_count || 0} goal={p.goal || 1000} theme={theme} />
            </div>
            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm font-bold shadow-sm" data-testid="signatures-count">
              <Users className="h-4 w-4 text-wine-600" />
              <span className="text-wine-700">{p.signatures_count}</span>
              <span className="text-neutral-500">{` signataire${p.signatures_count > 1 ? "s" : ""}`}</span>
            </div>

            {p.cover_image && <img src={p.cover_image} alt={p.title} className="mt-6 aspect-[16/9] w-full rounded-2xl object-cover shadow-md" />}

            <div className="mt-6 max-w-none text-neutral-700 leading-relaxed [&_p]:my-4 [&_p:first-child]:mt-0 [&_h1]:font-serif [&_h1]:text-3xl [&_h1]:text-navy [&_h1]:mb-3 [&_h1]:mt-6 [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:text-navy [&_h2]:mb-2 [&_h2]:mt-5 [&_h3]:font-serif [&_h3]:text-xl [&_h3]:text-navy [&_h3]:mb-2 [&_h3]:mt-4 [&_strong]:font-bold [&_strong]:text-navy [&_em]:italic [&_a]:text-wine-600 [&_a]:underline [&_a:hover]:text-wine-700 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-3 [&_li]:my-1 [&_blockquote]:border-l-4 [&_blockquote]:border-wine-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-neutral-600 [&_blockquote]:my-4 [&_code]:bg-neutral-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm">
              <ReactMarkdown remarkPlugins={[remarkBreaks]}>{p.description || ""}</ReactMarkdown>
            </div>
          </div>

          <aside className="space-y-4 self-start lg:sticky lg:top-20">
            {/* Sign form */}
            <div className="rounded-2xl border border-neutral-100 bg-white p-6 shadow-sm" data-testid="petition-sign-card">
              {signed ? (
                <div className="text-center" data-testid="petition-thank-you">
                  <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
                  <h3 className="mt-2 font-serif text-2xl text-navy">Merci pour votre signature !</h3>
                  <p className="mt-1 text-xs text-neutral-600">Votre voix compte. Aidez-nous à atteindre l&apos;objectif :</p>
                  <div className="mt-4 rounded-xl border border-neutral-100 bg-neutral-50 p-4">
                    <div className="font-serif text-4xl font-bold" style={{ color: theme }} data-testid="ty-counter">
                      {(p.signatures_count || 0).toLocaleString("fr")} <span className="text-lg text-neutral-400">/ {(p.goal || 1000).toLocaleString("fr")}</span>
                    </div>
                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white">
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(100, Math.round(((p.signatures_count || 0) / (p.goal || 1000)) * 100))}%`, backgroundColor: theme }} />
                    </div>
                    <p className="mt-2 text-[11px] uppercase tracking-wider font-bold" style={{ color: theme }}>
                      {Math.max(0, (p.goal || 1000) - (p.signatures_count || 0))} signatures restantes
                    </p>
                  </div>
                  <p className="mt-4 text-sm font-semibold text-navy">Multipliez l&apos;impact en partageant 👇</p>
                  <div className="mt-3"><ShareButtons url={url} title={p.title} slogan={p.share_slogan} /></div>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3">
                  <h3 className="font-serif text-xl text-navy">Signer la pétition</h3>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="sign-email" />
                  <div className="grid grid-cols-2 gap-2">
                    <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Prénom" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="sign-first-name" />
                    <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Nom" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="sign-last-name" />
                  </div>
                  <input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} placeholder="Organisation / Entreprise" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="sign-organization" />
                  <textarea rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Message court (optionnel)" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="sign-message" />
                  <label className="flex items-start gap-2 text-xs text-neutral-700">
                    <input type="checkbox" checked={form.allow_publish_name} onChange={(e) => setForm({ ...form, allow_publish_name: e.target.checked })} className="mt-0.5 accent-wine-500" data-testid="sign-publish-name" />
                    <span>J&apos;autorise l&apos;affichage public de mon nom dans la liste des signataires</span>
                  </label>
                  <label className="flex items-start gap-2 text-xs text-neutral-700">
                    <input type="checkbox" checked={form.newsletter_optin} onChange={(e) => setForm({ ...form, newsletter_optin: e.target.checked })} className="mt-0.5 accent-wine-500" data-testid="sign-newsletter" />
                    <span>Je souhaite recevoir la newsletter La WineTech</span>
                  </label>
                  <button type="submit" disabled={sending} className="w-full rounded-full bg-wine-600 px-5 py-3 text-sm font-bold text-white hover:bg-wine-700 disabled:opacity-50" style={{ backgroundColor: theme }} data-testid="sign-submit">
                    {sending ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : <><Send className="mr-1 inline h-3.5 w-3.5" /> Je signe</>}
                  </button>
                </form>
              )}
            </div>

            {/* Share + QR */}
            <div className="rounded-2xl border border-neutral-100 bg-white p-6 shadow-sm">
              <h3 className="font-serif text-base text-navy">Faire connaître</h3>
              <p className="mt-1 text-xs text-neutral-500">Partagez la pétition à votre réseau pour amplifier l&apos;impact.</p>
              <div className="mt-3"><ShareButtons url={url} title={p.title} slogan={p.share_slogan} /></div>
              <div className="mt-4 grid grid-cols-[auto_1fr] items-center gap-3 rounded-xl bg-neutral-50 p-3">
                <div className="rounded-lg bg-white p-2 shadow-sm">
                  <QRCodeSVG value={url} size={88} bgColor="#fff" fgColor={theme} />
                </div>
                <div className="text-[11px] text-neutral-600">
                  <strong>QR Code de la pétition</strong>
                  <p className="mt-0.5">Imprimez-le sur vos supports (flyers, salons, slides). Les visiteurs accèdent au formulaire en un scan.</p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Wall of signatures */}
      {p.signatures && p.signatures.length > 0 && (
        <section className="bg-neutral-50 py-12">
          <div className="mx-auto max-w-6xl px-6 md:px-8">
            <h2 className="font-serif text-2xl text-navy">Ils ont déjà signé</h2>
            <p className="mt-1 text-sm text-neutral-600">{p.signatures.length} signataire{p.signatures.length > 1 ? "s ont accepté que leur nom soit affiché" : " a accepté que son nom soit affiché"} publiquement</p>
            <div className="mt-6 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {p.signatures.map((s, i) => (
                <div key={i} className="flex items-start gap-3 rounded-xl border border-neutral-100 bg-white p-3" data-testid={`signature-${i}`}>
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-wine-100 text-xs font-bold text-wine-700">{initials(s.first_name, s.last_name)}</div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-navy truncate">{s.first_name} {s.last_name}</div>
                    {s.organization && <div className="text-xs text-neutral-500 truncate">{s.organization}</div>}
                    {s.message && <div className="mt-1 text-xs italic text-neutral-600"><MessageSquareQuote className="mr-1 inline h-3 w-3" />{s.message}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </PublicLayout>
  );
}
